import { useContext, useEffect, useState } from 'react';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Form,
	Input,
	message,
	Row,
	Select,
	Tag,
} from 'antd';
import { IoIosArrowDown } from 'react-icons/io';
import moment from 'moment';
import dayjs from 'dayjs';

import { useQuery } from '../../../hooks/useQuery';
import Context from '../../../context/Context';
import { api } from '../../../api/api';
import { isValidNric } from '../../../utils/utils';

import styles from './deactivateUser.module.scss';
import {
checkExistingUsernameApi,
} from '../../../api/userOnboarding';
import { Modal } from 'antd';
const DeactivateUser = () => {
	const [usernameStatus, setUsernameStatus] = useState(null);
	const { appAccess, SSPFacilities } = useContext(Context);
	const { query } = useQuery();
	const userId = query.get('id');
	const userDetails = JSON.parse(localStorage.getItem('user-onboard-data'));
	const fullName = localStorage.getItem("fullName");
	const [form] = Form.useForm();

	const isAdmin = userDetails?.isAdmin === 'Y';

	const [submit, setSubmit] = useState({
		loading: false,
		success: null,
		error: null,
	});

	const [userData, setUserData] = useState({
		loading: false,
		data: null,
		error: null,
	});

	//eslint-disable-next-line
	const [formValues, setFormValues] = useState(null);

	// Function to fetch user details
	const getUserDetails = async () => {
		if (!userId) return;

		setUserData({ loading: true, data: null, error: null });

		try {
			const response = await api.get({
				path: `/getUserDeactivationInfoById?id=${userId}`,
				service: 'useronboarding',
			});

			// Set API response to state
			setUserData({ loading: false, data: response.data, error: null });

			const formValues = {
				nhName: response.data.nhName || null,
				requestorDesignation: response.data.requestorDesignation || null,
				deactivateUserName: response.data.deactivateUserName || '',
				fullName: response.data.fullName || '',
				nric: response.data.nric || '',
				email: response.data.email || '',
				mobileNumber: response.data.mobileNumber || '',
				supervisorName: response.data.supervisorName || '',
				deactivateFrom: [
					response.data.hrDeactivation === true ? 'HR (AGHRM)' : null,
					response.data.financeDeactivation === true ? 'Finance (NAV)' : null,
					response.data.clinicalDeactivation === true ? 'Clinical (P6)' : null,
				],
				sspAccessRevoke:
					response.data.sspAccessRevoke
						?.split(',')
						.map((item) => item.trim()) || null,
				clinicalFacilityCode: response.data.clinicalFacilityCode
					? response.data.clinicalFacilityCode
							.split(',')
							.map((id) => Number(id.trim()))
					: [],
				financeFacilityCode: response.data.financeFacilityCode || null,
				hrFacilityCode: response.data.hrFacilityCode || null,
				remarks: response.data.remarks || '',
				deactivationDate: response.data.deactivationDate
					? dayjs(response.data.deactivationDate)
					: null,
				completionEmailList:
					response.data.completionEmailList || userDetails?.email,
			};

			// Set form values immediately after fetching data
			form.setFieldsValue(formValues);
		} catch (err) {
			setUserData({
				loading: false,
				data: null,
				error: err.message || 'Something went wrong',
			});
		}
	};

	useEffect(() => {
		getUserDetails(); // eslint-disable-next-line
	}, [userId]);

	const nhNameData = userDetails?.nhName
	? userDetails.nhName.split(',').map((item) => item.trim()).filter(Boolean)
	: [];
	const hasMultipleNH = nhNameData.length > 1;
	// SSP Facility Code
	const sspFacilityCode = SSPFacilities.data?.filter(
		(el) =>
			el.nhName?.trim().toLowerCase() ===
			form.getFieldValue('nhName')?.trim()?.toLowerCase()
	);

	// Clinical Facility Code
	const clinicalFacilityCode = SSPFacilities.data
		?.filter(
			(el) =>
				el.nhName?.trim().toLowerCase() ===
				form.getFieldValue('nhName')?.trim()?.toLowerCase()
		)
		.map((el) => ({
			label: el.facilityName,
			value: Number(el.clinicalFacilityCode),
		}));
		const nhAdminFacilities = isAdmin
			? 'ALL Facilities'
			: userDetails?.facilityCode
				?.split(',')
				.map(code => code.trim())
				.filter(code => code !== 'UAM')   // remove UAM
				.join(',');
	const onFinish = (values) => {
		setSubmit({ loading: true, success: null, error: null });

		const formdataObject = {
			...values,
			nhAdminFacilities,
			nhName: values.nhName?.trim(),
			requestorDesignation: values.requestorDesignation?.trim(),
			deactivateUserName: values.deactivateUserName?.trim(),
			fullName: values.fullName,
			nric: values.nric,
			email: values.email,
			mobileNumber: values.mobileNumber || null,
			supervisorName: values.supervisorName || null,
			hrDeactivation:
				values.deactivateFrom && values.deactivateFrom.includes('HR (AGHRM)')
					? 1
					: 0,
			financeDeactivation:
				values.deactivateFrom && values.deactivateFrom.includes('Finance (NAV)')
					? 1
					: 0,
			clinicalDeactivation:
				values.deactivateFrom && values.deactivateFrom.includes('Clinical (P6)')
					? 1
					: 0,
			financeFacilityCode: values.financeFacilityCode,
			hrFacilityCode: values.hrFacilityCode,
			clinicalFacilityCode:
				values.clinicalFacilityCode?.length > 0
					? values.clinicalFacilityCode.join(',')
					: '',
			clinicalFacilityCodeDesc:
				values.clinicalFacilityCode?.length > 0
					? values.clinicalFacilityCode
							.map((code) => {
								const facility = clinicalFacilityCode.find(
									(item) => item.value === code
								);
								return facility ? facility.label : null;
							})
							.filter(Boolean)
							.join(',')
					: '',
			deactivationDate: values.deactivationDate
				? dayjs(values.deactivationDate).format('YYYY-MM-DD') + 'T00:00:00'
				: null,
			createdBy: userDetails?.userId,
			status: 'P',
		};

		// Process keys with array values to convert them into comma-separated strings
		Object.keys(formdataObject).forEach((key) => {
			if (Array.isArray(formdataObject[key])) {
				formdataObject[key] = formdataObject[key].join(', ');
			}
		});

		if (userId && userData?.data?.id) {
			formdataObject.id = userData.data.id;
			formdataObject.modifiedBy = userDetails?.userId;
		}

		delete formdataObject.deactivateFrom;

		api
			.post({
				path: '/makeruserdeactivation',
				service: 'useronboarding',
				formdata: [formdataObject],
			})
			.then((response) => {
				setSubmit({
					loading: false,
					success: 'User creation request submitted successfully!',
					error: null,
				});
				message.success(
					`User ${
						userId ? 'update' : 'deactivate'
					} request submitted successfully!`
				);
				form.resetFields();
			})
			.catch((err) => {
				console.error('Error:', err.message || 'Something went wrong');
				setSubmit({
					loading: false,
					success: null,
					error:
						err.message ||
						`Failed to ${
							userId ? 'update' : 'create'
						} user request please drop email to Admin!`,
				});
				message.error(
					`Failed to ${
						userId ? 'update' : 'deactivate'
					} user request please drop email to Admin!`
				);
				form.resetFields();
			});
	};

	const tagRender = (props) => {
		const { label, closable, onClose } = props;
		const onPreventMouseDown = (event) => {
			event.preventDefault();
			event.stopPropagation();
		};

		return (
			<Tag
				color={'#ff6a95'}
				onMouseDown={onPreventMouseDown}
				closable={closable}
				onClose={onClose}
				style={{
					marginInlineEnd: 4,
				}}
			>
				{label}
			</Tag>
		);
	};

	const initialValues = userId
		? {}
		: {
				completionEmailList: userDetails?.email,
				nhName: isAdmin ? null : (hasMultipleNH ? null : nhNameData[0] || null),
		  };

	const isNricMandatory = (selectedAppAccess) => {
		form.validateFields(['nric']);
		return appAccess.data?.some(
			(option) =>
				selectedAppAccess.includes(option.nhName) &&
				option.nricMandatory === true
		);
	};

	const isMobileMandatory = (selectedAppAccess) => {
		form.validateFields(['mobileNumber']);
		return appAccess.data?.some(
			(option) =>
				selectedAppAccess.includes(option.nhName) &&
				option.mobileNoMandatory === true
		);
	};

	return (
		<div className={styles['container']}>
			<div className={styles['wrapper']}>
				<div className={styles['intro-para']}>

				{/* Centered page title block */}
				<div className={styles['header-block']}>
					<h1 className={styles['page-title']}>
					User <span>Deactivation</span>
					</h1>

					<p className={styles['subtitle']}>
					Securely deactivate users from the system
					</p>
				</div>

				{/* Left-aligned content */}
				<div className={styles['intro-left']}>
					<p className={styles['head-text']}>
					Dear {fullName ?? userDetails?.userId},
					</p>

					<p className={styles['head-text']}>
					Please {userId ? 'update' : 'fill'} the below form to deactivate a user.
					</p>
				</div>

				</div>


				<Form
					id="deactivate-user-form"
					name="deactivate-user-form"
					form={form}
					labelCol={{ span: 24 }}
					disabled={
						submit.loading ||
						appAccess.error?.length > 0 ||
						SSPFacilities.error?.length > 0 ||
						userData.loading
					}
					onFinish={onFinish}
					validateTrigger={['onBlur', 'onChange']}
					onValuesChange={(changedValues, allValues) => {
						setFormValues(allValues);
						form.validateFields(['mobileNumber']);
					}}
					initialValues={initialValues}
				>
					<Row gutter={20}>
						{/* Nursing Home Name */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Nursing Home Name"
								name="nhName"
								rules={[
									{
										required: true,
										message: 'Please select Nursing Home Name!',
									},
								]}
							>
								{isAdmin || hasMultipleNH ? (
									<Select
										tagRender={tagRender}
										placeholder="Please select Nursing Home Name"
										suffixIcon={<IoIosArrowDown style={{ fontSize: '14px' }} />}
										options={nhNameData?.map((item) => ({
											label: item,
											value: item,
										}))}
										onChange={() => {
											form.setFieldsValue({
												hrFacilityCode: null,
												financeFacilityCode: null,
												clinicalFacilityCode: null,
												deactivateFrom: null,
											});
										}}
									/>
								) : (
									<Input placeholder="Nursing Home Name" disabled />
								)}
							</Form.Item>
						</Col>
						{/* Requestor Designation */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Requestor Designation"
								name="requestorDesignation"
								tooltip={"Provide your designation as the person requesting the deactivation."}
								rules={[
									{
										required: true,
										message: 'Please enter Requestor Designation!',
									},
								]}
							>
								<Input placeholder="Please enter Requestor Designation" />
							</Form.Item>
						</Col>
						{/* ECCareSuite User ID */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="ECCareSuite User ID"
								name="deactivateUserName"
								validateTrigger="onBlur"
								validateStatus={usernameStatus === "valid" ? "success" : undefined}
								help={
									usernameStatus === "valid"
										? <span style={{ color: "green" }}>✔ Valid Username</span>
										: null
								}
								tooltip={"Provide the User ID of the user whose access needs to be removed from the system."}
								rules={[
									{
										required: true,
										message: 'Please enter ECCareSuite User ID!',
									},
									{
										validator: async (_, value) => {
											if (!value || !value.trim()) {
												setUsernameStatus(null);
												return Promise.resolve();
											}

											const username = value.trim();

											try {
												const result = await checkExistingUsernameApi(username);

												const exists =
													result === true ||
													result?.exists === true ||
													result?.data === true ||
													result?.valid === true;

												if (!exists) {
													 setUsernameStatus("invalid");
													form.setFieldsValue({ deactivateUserName: null });

													Modal.error({
														title: 'Username Not Found',
														content: 'Entered username does not exist. Enter correct username to Deactivate',
														okText: 'Close',
													});
													
													return Promise.reject(
														new Error('Entered username does not exist. Enter correct username to Deactivate')
													);
												}
                                                setUsernameStatus("valid");
												return Promise.resolve();

											} catch (err) {
												setUsernameStatus(null);
												return Promise.reject(
													new Error(err?.message || 'Unable to validate username')
												);
											}
										},
									},
								]}
							>
								<Input placeholder="Please enter ECCareSuite User ID" />
							</Form.Item>
						</Col>
						{/* Full Name  */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Full Name"
								name="fullName"
								tooltip={"Enter the full name of the user whose system access needs to be removed."}
								rules={[
									{
										required: true,
										message: 'Please input Full Name!',
									},
								]}
							>
								<Input placeholder="Please input Full Name!" />
							</Form.Item>
						</Col>
						{/* NRIC / FIN */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="NRIC / FIN"
								name="nric"
								tooltip={"Enter the NRIC of the deactivating user"}
								rules={[
									{
										required: formValues?.nhName
											? isNricMandatory(formValues.nhName)
											: false,
										message: 'Please input NRIC / FIN!',
									},
									{
										validator: (_, value) => {
											if (!value || isValidNric(value)) {
												return Promise.resolve();
											}
											return Promise.reject(new Error('Invalid NRIC / FIN!'));
										},
									},
								]}
							>
								<Input placeholder="Please input NRIC / FIN!" />
							</Form.Item>
						</Col>
						{/* Email Address */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Email Address"
								name="email"
								tooltip={"Enter the Email ID of the deactivating user"}
								rules={[
									{
										type: 'email',
										whitespace: true,
										message: 'Please enter valid email!',
									},
								]}
							>
								<Input type="email" placeholder="Please input Email Address!" />
							</Form.Item>
						</Col>
						{/* Mobile Number */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Mobile Number"
								name="mobileNumber"
								tooltip={"Enter the Mobile No. of the deactivating user"}
								rules={[
									{
										required: form.getFieldValue('nhName')
											? isMobileMandatory(form.getFieldValue('nhName'))
											: false,
										message: 'Please input Mobile Number!',
									},
									...(form.getFieldValue('nhName') &&
									isMobileMandatory(form.getFieldValue('nhName'))
										? [
												{
													validator: (_, value) => {
														if (value && String(value).length === 8) {
															return Promise.resolve();
														}
														return Promise.reject(
															new Error('Please enter a valid Mobile Number!')
														);
													},
												},
										  ]
										: []),
								]}
							>
								<Input
									type="number"
									placeholder="Please input Mobile Number!"
								/>
							</Form.Item>
						</Col>
						{/* Supervisor/Manager's Name */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Supervisor/Manager's Name "
								name="supervisorName"
								tooltip={"Enter the supervisor's name of the deactivating user."}
								rules={[
									{
										required: true,
										message: "Please input Supervisor/Manager's Name!",
									},
								]}
							>
								<Input placeholder="Please input Supervisor/Manager's Name" />
							</Form.Item>
						</Col>
						{/* Deactivate from */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Deactivate from"
								name="deactivateFrom"
								tooltip={"Select the module to deactivate. Choose only the module(s) the user currently has access to. Do not select all."}								
								rules={[
									{
										required: sspFacilityCode?.some(
											(item) =>
												item?.hrFacilityCode !== null ||
												item?.financeFacilityCode !== null ||
												item?.clinicalFacilityCode !== null
										),
										message: 'Please select at least one access!',
									},
								]}
							>
								<Checkbox.Group
									disabled={!form.getFieldValue([['nhName']])}
									options={[
										{
											value: 'Clinical (P6)',
											label: 'Clinical (P6)',
											disabled: !sspFacilityCode?.some(
												(item) => item?.clinicalFacilityCode !== null
											),
										},
										{
											value: 'Finance (NAV)',
											label: 'Finance (NAV)',
											disabled: !sspFacilityCode?.some(
												(item) => item?.financeFacilityCode !== null
											),
										},
										{
											value: 'HR (AGHRM)',
											label: 'HR (AGHRM)',
											disabled: !sspFacilityCode?.some(
												(item) => item?.hrFacilityCode !== null
											),
										},
									]}
								/>
							</Form.Item>
						</Col>
						{/* Revoke Access to SSP Facility Code */}
						{/* <Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="SSP Facility Code"
								name="sspAccessRevoke"
								tooltip={
									!form.getFieldValue([['nhName']]) &&
									'Please select Nursing Home Name'
								}
								rules={[
									{
										required: true,
										message: 'Please select SSP Facility Code!',
									},
								]}
							>
								<Select
									mode="multiple"
									tagRender={tagRender}
									allowClear
									placeholder="Please select SSP Facility Code"
									suffixIcon={<IoIosArrowDown style={{ fontSize: '14px' }} />}
									options={sspFacilityCode?.map((item) => ({
										label: item?.facilityName,
										value: item?.facilityName,
									}))}
									disabled={!form.getFieldValue([['nhName']])}
								/>
							</Form.Item>
						</Col> */}

						{/* Revoke Access to Clinical Facility */}
						{form
							.getFieldValue([['deactivateFrom']])
							?.includes('Clinical (P6)') && (
							<Col xs={24} md={12} xxl={8}>
								<Form.Item
									label="Revoke Access to Clinical Facility"
									name="clinicalFacilityCode"
									tooltip={"Select the clinical facility where access should be removed. Select multiple if the user has access to multiple facilities."}
									rules={[
										{
											required: true,
											message: 'Please select Clinical Facility!',
										},
									]}
								>
									<Select
										mode="multiple"
										tagRender={tagRender}
										allowClear
										placeholder="Please select Clinical Facility"
										optionFilterProp={'label'}
										suffixIcon={<IoIosArrowDown style={{ fontSize: '14px' }} />}
										disabled={
											!sspFacilityCode?.some(
												(item) => item?.clinicalFacilityCode !== null
											)
										}
										options={clinicalFacilityCode}
									/>
								</Form.Item>
							</Col>
						)}
						{/* Revoke Access to Finance Facility Code */}
						{form
							.getFieldValue('deactivateFrom')
							?.includes('Finance (NAV)') && (
							<Col xs={24} md={12} xxl={8}>
								<Form.Item
									label="Revoke Access to Finance Facility Code"
									name="financeFacilityCode"
								>
									<Select
										allowClear
										optionFilterProp={'label'}
										// labelInValue={true}
										showSearch
										placeholder="Please select Finance Facility code"
										suffixIcon={<IoIosArrowDown style={{ fontSize: '14px' }} />}
										disabled={
											!sspFacilityCode?.some(
												(item) => item?.financeFacilityCode !== null
											)
										}
										options={[
											...new Map(
												sspFacilityCode
													?.filter((item) => item?.financeFacilityCode !== null)
													.map((item) => [
														item.financeFacilityCode,
														{
															label: item.financeFacilityCode,
															value: item.financeFacilityCode,
														},
													])
											).values(),
										]}
									/>
								</Form.Item>
							</Col>
						)}
						{/* Revoke Access to HR Facility Code */}
						{form.getFieldValue('deactivateFrom')?.includes('HR (AGHRM)') && (
							<Col xs={24} md={12} xxl={8}>
								<Form.Item
									label="Revoke Access to HR Facility Code"
									name="hrFacilityCode"
								>
									<Select
										allowClear
										placeholder="Please select HR FacilityCode"
										suffixIcon={<IoIosArrowDown style={{ fontSize: '14px' }} />}
										disabled={
											!sspFacilityCode?.some(
												(item) => item?.hrFacilityCode !== null
											)
										}
										options={sspFacilityCode
											?.filter((item) => item?.hrFacilityCode !== null)
											.map((item) => ({
												label: item?.hrFacilityCode,
												value: item?.hrFacilityCode,
											}))}
									></Select>
								</Form.Item>
							</Col>
						)}
						{/* Remarks */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item label="Remarks" name="remarks">
								<Input.TextArea
									placeholder="Please input Remarks!"
									autoSize={{
										minRows: 3,
										maxRows: 5,
									}}
								/>
							</Form.Item>
						</Col>
						{/* Date of Deactivation */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Date of Deactivation"
								name="deactivationDate"
								tooltip={"Select the date when the user's system access should be deactivated."}
								rules={[
									{
										required: true,
										message: 'Please select Date of Deactivation.',
									},
								]}
							>
								<DatePicker
									style={{ width: '100%' }}
									format="YYYY-MM-DD"
									disabledDate={(current) => {
										return current && current.isBefore(moment().startOf('day'));
									}}
									placeholder="Select Date of Deactivation"
								/>
							</Form.Item>
						</Col>
						{/* Confirmation Email Recipient(s) */}
						<Col xs={24} md={12} xxl={8}>
							<Form.Item
								label="Confirmation Email Recipient(s)"
								name="completionEmailList"
								tooltip="Enter the email addresses that should receive the confirmation notification. Separate multiple emails with commas."
								rules={[
									{
										required: true,
										message: 'At least one email is required.',
									},
									{
										validator: (_, value) => {
											if (!value) {
												return Promise.resolve();
											}

											// Split the string by commas and trim spaces
											const emails = value
												.split(',')
												.map((email) => email.trim());

											// Check if every email is valid
											const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
											const invalidEmails = emails.filter(
												(email) => !emailRegex.test(email)
											);

											if (invalidEmails.length > 0) {
												return Promise.reject(
													`Invalid emails: ${invalidEmails.join(', ')}`
												);
											}

											// Check for duplicate emails
											const emailCounts = emails.reduce((acc, email) => {
												acc[email] = (acc[email] || 0) + 1;
												return acc;
											}, {});
											const duplicateEmails = Object.keys(emailCounts).filter(
												(email) => emailCounts[email] > 1
											);

											if (duplicateEmails.length > 0) {
												return Promise.reject(
													`Duplicate emails: ${duplicateEmails.join(', ')}`
												);
											}

											return Promise.resolve();
										},
									},
								]}
							>
								<Input.TextArea
									autoSize={{
										minRows: 3,
										maxRows: 5,
									}}
									placeholder="Please input Completion Email List"
								/>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Form.Item style={{ marginBottom: 0 }}>
								<Button
									className="premium-btn"
									loading={submit.loading}
									type="primary"
									htmlType="submit"
									block
								>
									{userId ? 'Update' : 'Deactivate'} User
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</div>
		</div>
	);
};

export default DeactivateUser;
